/**
 * OrgSwitcher — sidebar + mobile-header indicator/picker for the active
 * organization. Always visible so the user knows which org's data the rest
 * of the app is currently showing. SKOOL-24.
 *
 * - 0 orgs: renders nothing.
 * - 1 org: shows the org name as a static indicator (no dropdown).
 * - 2+ orgs: clickable button that opens a dropdown of all orgs.
 *
 * Variants:
 *   `variant="sidebar"`  — full block in the sidebar above the nav
 *   `variant="collapsed"` — single icon when the sidebar is collapsed
 *   `variant="mobile"`    — compact pill for the mobile header
 */

import { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import type { Organization } from '@tss/shared';

interface OrgSwitcherProps {
  organizations: Organization[];
  currentOrg: Organization | null;
  onSelectOrg: (org: Organization) => void;
  variant?: 'sidebar' | 'collapsed' | 'mobile';
}

export function OrgSwitcher({
  organizations,
  currentOrg,
  onSelectOrg,
  variant = 'sidebar',
}: OrgSwitcherProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  if (!organizations || organizations.length === 0 || !currentOrg) return null;

  const multipleOrgs = organizations.length > 1;

  // Collapsed sidebar: single icon, click expands the picker as a floating menu
  if (variant === 'collapsed') {
    return (
      <div
        ref={ref}
        style={{ margin: '0 8px 12px', position: 'relative' }}
        title={multipleOrgs ? `Active: ${currentOrg.name} — click to switch` : currentOrg.name}
      >
        <button
          type="button"
          onClick={() => multipleOrgs && setOpen((o) => !o)}
          disabled={!multipleOrgs}
          style={{
            width: '100%',
            padding: '8px',
            background: 'rgba(99, 102, 241, 0.12)',
            border: '1px solid rgba(99, 102, 241, 0.25)',
            borderRadius: '8px',
            cursor: multipleOrgs ? 'pointer' : 'default',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Building2 size={18} style={{ color: '#6366f1' }} />
        </button>
        {open && (
          <Dropdown
            organizations={organizations}
            currentOrg={currentOrg}
            onSelect={(org) => {
              onSelectOrg(org);
              setOpen(false);
            }}
            anchor="left-of-collapsed"
          />
        )}
      </div>
    );
  }

  // Mobile header pill
  if (variant === 'mobile') {
    return (
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => multipleOrgs && setOpen((o) => !o)}
          disabled={!multipleOrgs}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            background: 'rgba(99, 102, 241, 0.12)',
            border: '1px solid rgba(99, 102, 241, 0.25)',
            borderRadius: '999px',
            cursor: multipleOrgs ? 'pointer' : 'default',
            color: 'var(--gray-900)',
            fontSize: '13px',
            fontWeight: 500,
            maxWidth: '180px',
            overflow: 'hidden',
          }}
        >
          <Building2 size={14} style={{ color: '#6366f1', flexShrink: 0 }} />
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {currentOrg.name}
          </span>
          {multipleOrgs && <ChevronDown size={14} style={{ color: '#6366f1', flexShrink: 0 }} />}
        </button>
        {open && (
          <Dropdown
            organizations={organizations}
            currentOrg={currentOrg}
            onSelect={(org) => {
              onSelectOrg(org);
              setOpen(false);
            }}
            anchor="below"
          />
        )}
      </div>
    );
  }

  // Default: full sidebar block
  return (
    <div ref={ref} style={{ margin: '0 12px 12px', position: 'relative' }}>
      <button
        type="button"
        onClick={() => multipleOrgs && setOpen((o) => !o)}
        disabled={!multipleOrgs}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: 'rgba(99, 102, 241, 0.10)',
          border: '1px solid rgba(99, 102, 241, 0.25)',
          borderRadius: '8px',
          cursor: multipleOrgs ? 'pointer' : 'default',
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        <span
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#4f46e5',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Building2 size={12} />
          Active organization
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            color: 'var(--gray-900)',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {currentOrg.name}
          </span>
          {multipleOrgs && <ChevronDown size={14} style={{ color: '#6366f1', flexShrink: 0 }} />}
        </span>
      </button>
      {open && (
        <Dropdown
          organizations={organizations}
          currentOrg={currentOrg}
          onSelect={(org) => {
            onSelectOrg(org);
            setOpen(false);
          }}
          anchor="below"
        />
      )}
    </div>
  );
}

function Dropdown({
  organizations,
  currentOrg,
  onSelect,
  anchor,
}: {
  organizations: Organization[];
  currentOrg: Organization;
  onSelect: (org: Organization) => void;
  anchor: 'below' | 'left-of-collapsed';
}) {
  const positionStyle: React.CSSProperties =
    anchor === 'left-of-collapsed'
      ? { position: 'absolute', left: '100%', top: 0, marginLeft: '8px', minWidth: '200px' }
      : { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px' };

  return (
    <div
      role="listbox"
      style={{
        ...positionStyle,
        background: 'white',
        border: '1px solid var(--gray-200)',
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        zIndex: 50,
        overflow: 'hidden',
      }}
    >
      {organizations.map((org) => {
        const isCurrent = org.org_id === currentOrg.org_id;
        return (
          <button
            key={org.org_id}
            type="button"
            role="option"
            aria-selected={isCurrent}
            onClick={() => onSelect(org)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              width: '100%',
              padding: '10px 12px',
              background: isCurrent ? 'rgba(99, 102, 241, 0.08)' : 'white',
              border: 'none',
              borderBottom: '1px solid var(--gray-100)',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '13px',
              color: 'var(--gray-900)',
              fontWeight: isCurrent ? 600 : 500,
            }}
          >
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {org.name}
            </span>
            {isCurrent && <Check size={14} style={{ color: '#6366f1', flexShrink: 0 }} />}
          </button>
        );
      })}
    </div>
  );
}
